import React, { useCallback, useEffect, useRef, useState } from "react";
import { useColors } from "../context/ThemeContext";

const AUTO_MS = 3500;
const IDLE_RESUME_MS = 4500;
const MAX_EVENTS = 5;

const formatDateParts = (startsAt) => {
  if (!startsAt) return { day: "—", month: "" };
  const d = new Date(startsAt);
  return {
    day: String(d.getDate()),
    month: d.toLocaleDateString("en-NG", { month: "short" }),
  };
};

const formatWhenShort = (startsAt, location) => {
  if (!startsAt) return location || "";
  const d = new Date(startsAt);
  const date = d.toLocaleDateString("en-NG", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
  return location ? `${date} · ${location}` : date;
};

export default function UpcomingEventsStrip({ events = [], onViewAll, onEventSelect }) {
  const C = useColors();
  const list = (events || []).slice(0, MAX_EVENTS);
  const scrollerRef = useRef(null);
  const cardRefs = useRef([]);
  const pausedRef = useRef(false);
  const userScrollingRef = useRef(false);
  const resumeTimerRef = useRef(null);
  const activeRef = useRef(0);
  const [active, setActive] = useState(0);

  const setActiveIndex = useCallback((index) => {
    activeRef.current = index;
    setActive(index);
  }, []);

  const syncActiveFromScroll = useCallback(() => {
    const el = scrollerRef.current;
    if (!el || !list.length) return;
    const mid = el.scrollLeft + el.clientWidth / 2;
    let best = 0;
    let bestDist = Infinity;
    cardRefs.current.forEach((card, i) => {
      if (!card) return;
      const center = card.offsetLeft + card.offsetWidth / 2;
      const dist = Math.abs(center - mid);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    });
    setActiveIndex(best);
  }, [list.length, setActiveIndex]);

  const scrollToIndex = useCallback((index, behavior = "smooth") => {
    const card = cardRefs.current[index];
    const el = scrollerRef.current;
    if (!card || !el) return;
    el.scrollTo({ left: card.offsetLeft - 4, behavior });
    setActiveIndex(index);
  }, [setActiveIndex]);

  const pauseForUser = useCallback(() => {
    pausedRef.current = true;
    userScrollingRef.current = true;
    if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
    resumeTimerRef.current = setTimeout(() => {
      userScrollingRef.current = false;
      pausedRef.current = false;
    }, IDLE_RESUME_MS);
  }, []);

  useEffect(() => {
    if (list.length <= 1) return undefined;
    const id = setInterval(() => {
      if (pausedRef.current || userScrollingRef.current) return;
      const next = (activeRef.current + 1) % list.length;
      scrollToIndex(next);
    }, AUTO_MS);
    return () => clearInterval(id);
  }, [list.length, scrollToIndex])

  useEffect(() => () => {
    if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
  }, []);

  if (!list.length) return null;

  return (
    <div
      style={{
        background: C.card,
        border: `1px solid ${C.border}`,
        borderRadius: 22,
        padding: "18px 0 14px",
        marginBottom: 16,
        overflow: "hidden",
      }}
      onMouseEnter={() => { pausedRef.current = true; }}
      onMouseLeave={() => {
        if (!userScrollingRef.current) pausedRef.current = false;
      }}
      onFocusCapture={() => { pausedRef.current = true; }}
      onBlurCapture={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget) && !userScrollingRef.current) {
          pausedRef.current = false;
        }
      }}
    >
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 14,
        padding: "0 18px",
      }}>
        <p style={{ color: C.text, fontSize: 14, fontWeight: 700, margin: 0 }}>Upcoming Events</p>
        <button
          type="button"
          onClick={onViewAll}
          style={{
            background: "none",
            border: "none",
            color: C.lime,
            fontSize: 11,
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          View all
        </button>
      </div>

      <div
        ref={scrollerRef}
        className="pp-scroll-x"
        onScroll={() => {
          syncActiveFromScroll();
          pauseForUser();
        }}
        style={{
          display: "flex",
          gap: 12,
          padding: "0 18px",
          scrollSnapType: "x mandatory",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {list.map((ev, i) => {
          const { day, month } = formatDateParts(ev.startsAt);
          return (
            <button
              key={ev.id}
              type="button"
              ref={(node) => { cardRefs.current[i] = node; }}
              onClick={() => onEventSelect?.(ev)}
              style={{
                flex: "0 0 248px",
                width: 248,
                scrollSnapAlign: "start",
                textAlign: "left",
                border: `1px solid ${C.border}`,
                borderRadius: 18,
                padding: 0,
                overflow: "hidden",
                cursor: "pointer",
                background: C.cardMid || C.surface,
                color: C.text,
                display: "flex",
                flexDirection: "column",
                transition: "transform 0.2s ease, border-color 0.2s ease",
              }}
            >
              <div style={{
                position: "relative",
                height: 110,
                background: `linear-gradient(145deg, ${C.forestBright} 0%, ${C.forest} 100%)`,
                overflow: "hidden",
              }}>
                {ev.coverImageUrl ? (
                  <img
                    src={ev.coverImageUrl}
                    alt=""
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                ) : (
                  <div style={{
                    position: "absolute",
                    inset: 0,
                    background: `
                      radial-gradient(ellipse at 20% 80%, ${C.lime}22 0%, transparent 55%),
                      radial-gradient(ellipse at 90% 10%, ${C.info || C.lime}18 0%, transparent 45%)
                    `,
                  }} />
                )}
                <div style={{
                  position: "absolute",
                  top: 10,
                  left: 10,
                  minWidth: 44,
                  padding: "6px 8px",
                  borderRadius: 12,
                  background: "rgba(11, 17, 32, 0.78)",
                  backdropFilter: "blur(8px)",
                  WebkitBackdropFilter: "blur(8px)",
                  textAlign: "center",
                  border: `1px solid ${C.borderBright || C.border}`,
                }}>
                  <div style={{
                    fontSize: 16,
                    fontWeight: 900,
                    lineHeight: 1,
                    color: C.text,
                    letterSpacing: "-0.02em",
                  }}>{day}</div>
                  <div style={{
                    fontSize: 9,
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: C.lime,
                    marginTop: 3,
                  }}>{month}</div>
                </div>
              </div>

              <div style={{ padding: "12px 14px 14px", flex: 1, minWidth: 0 }}>
                <div style={{
                  fontWeight: 800,
                  fontSize: 14,
                  lineHeight: 1.25,
                  marginBottom: 6,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}>{ev.title}</div>
                <div style={{
                  color: C.muted,
                  fontSize: 11,
                  lineHeight: 1.35,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}>{formatWhenShort(ev.startsAt, ev.location)}</div>
                {ev.tag ? (
                  <div style={{
                    display: "inline-block",
                    marginTop: 8,
                    padding: "3px 8px",
                    borderRadius: 999,
                    background: C.limeDim,
                    color: C.lime,
                    fontSize: 10,
                    fontWeight: 700,
                  }}>{ev.tag}</div>
                ) : null}
              </div>
            </button>
          );
        })}
      </div>

      {list.length > 1 && (
        <div style={{
          display: "flex",
          justifyContent: "center",
          gap: 6,
          marginTop: 12,
          padding: "0 18px",
        }}>
          {list.map((ev, i) => (
            <button
              key={ev.id}
              type="button"
              aria-label={`Go to event ${i + 1}`}
              onClick={() => {
                pauseForUser();
                scrollToIndex(i);
              }}
              style={{
                width: i === active ? 16 : 6,
                height: 6,
                borderRadius: 999,
                border: "none",
                padding: 0,
                cursor: "pointer",
                background: i === active ? C.lime : C.borderBright || C.border,
                transition: "width 0.25s ease, background 0.25s ease",
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
